/**
 * drawings.ts — the drawing layer's DECISIONS. Pure, no DOM, no ICT rule.
 *
 * Andrei, 2026-07-28: *"like in tradingview i need to have an ability to do basic drawing
 * on the chart that should survive app restarts and ability to delete my drawings: like
 * basic trend line, horizontal line, fib retracement with extensions, rectangle. Should
 * have magnet mode snaps drawings mode to make them precise."*
 *
 * WHAT IS **NOT** IN THIS FILE, AND WHY THAT IS THE WHOLE DESIGN
 * -------------------------------------------------------------
 * The fib. `main.ts`'s first rule — the one the app exists to protect — is "no fractal
 * width, no ATR multiple, **no fib ratio**, no threshold of any kind may appear in this
 * codebase". `ict/ranges.py::retrace_price` already owns that arithmetic, and it carries
 * a trap the obvious frontend version walks into:
 *
 *     fib 1.0 = origin (where the leg came FROM), fib 0.0 = terminus (where it ran TO)
 *     "ORDER MATTERS AND IS NOT hi/lo … handing this `(hi, lo)` inverts every long"
 *
 * Not hypothetical here: the OTE band shipped applying `ote_zone(hi, lo)`
 * unconditionally and was on the **wrong side of EQ on 69.6% of degree-1 series**. So a
 * fib's `levels` arrive from `/api/drawings/…` as `{ratio, price}` pairs computed by
 * `ict.ranges.fib_levels`, and this file only decides where the labels go.
 *
 * WHAT **IS** HERE: the model, and three decisions a test must be able to reach —
 * `snapToBar` (magnet), `hitTestDrawing` (select / delete) and `drawingGeometry`
 * (payload → shapes). They live in a module rather than in `main.ts` because gate 5
 * (vitest) exists precisely because a state bug inside `main.ts` was unreachable by
 * every other gate.
 *
 * MAGNET IS A MECHANISM, NOT A THRESHOLD. Andrei proposes mechanisms and deletes knobs
 * (he had `fractal_n` removed outright and countered a width control with "how about
 * pointer, processing candles one after another?"). So there is deliberately no "snap
 * radius": the bar under the cursor is already unambiguous from the time scale, so the
 * anchor snaps to the nearest of THAT bar's four prices — nearest-of-four needs no
 * radius at all. Magnet is on or off.
 */

/** The four tools in scope. Mirrors `chartapp/api/drawings.py::KINDS`. */
export type DrawingKind = 'trendline' | 'hline' | 'rect' | 'fib';

/** A point in chart space. `time` is a UTC epoch SECOND, as the bars are. */
export type Anchor = { time: number; price: number };

/** One fib level, computed by `ict.ranges.fib_levels` server-side. */
export type FibLevel = { ratio: number; price: number };

/** A stored drawing, exactly as `/api/drawings/{asset}/{tf}` serves it. */
export type Drawing = {
  id: string;
  kind: DrawingKind;
  anchors: Anchor[];
  /** Whether THIS drawing's anchors were snapped — provenance, not just a UI mode. */
  magnet: boolean;
  /** `fib` only, and never computed here. */
  levels?: FibLevel[];
};

/** `GET /api/drawings/{asset}/{tf}`. `skipped`/`note` are contract, not diagnostics. */
export type DrawingsPayload = {
  asset: string;
  tf: string;
  schema_version: string;
  drawings: Drawing[];
  /** Malformed stored drawings dropped on load. `-1` = the whole file was unusable. */
  skipped: number;
  note: string | null;
};

/** One bar, as much of it as the magnet needs. */
export type BarOHLC = { time: number; open: number; high: number; low: number; close: number };

/** How close (CSS px) the cursor must be to select a drawing. Selection only. */
export const HIT_TOLERANCE_PX = 6;

/**
 * MAGNET — snap a price to the nearest of the bar's own O/H/L/C. No radius.
 *
 * Returns the RAW price unchanged when magnet is off, so both states are one code path
 * and a caller cannot forget to honour the flag. `bar === null` (the cursor is past the
 * last bar, in the chart's future space) also returns the raw price: there is nothing to
 * snap to there, and inventing an anchor on a bar that does not exist would be worse
 * than an unsnapped one.
 *
 * WHY NOT "the nearest OHLC within N pixels": that N is a knob, and a knob calibrated on
 * one instrument's scale is wrong on another (this app charts BTC near 100,000 and ETHBTC
 * near 0.03 in the same session). The bar is already decided by the cursor's x, so
 * nearest-of-four on THAT bar is exact and needs nothing to tune.
 */
export function snapToBar(price: number, bar: BarOHLC | null, magnet: boolean): number {
  if (!magnet || bar === null) return price;
  const candidates = [bar.open, bar.high, bar.low, bar.close].filter((p) => Number.isFinite(p));
  if (!candidates.length) return price;
  let best = candidates[0];
  let bestD = Math.abs(price - best);
  for (const c of candidates.slice(1)) {
    const d = Math.abs(price - c);
    // Strict `<`: ties keep the EARLIER candidate, so the result is deterministic in
    // O/H/L/C order rather than depending on iteration accidents.
    if (d < bestD) { best = c; bestD = d; }
  }
  return best;
}

/** Snap an anchor's time to the bar under it, keeping the price rule identical. */
export function snapAnchor(a: Anchor, bar: BarOHLC | null, magnet: boolean): Anchor {
  if (!magnet || bar === null) return a;
  return { time: bar.time, price: snapToBar(a.price, bar, magnet) };
}

/** The bar whose time is nearest `time`, or null when there are none. */
export function barAt(bars: readonly BarOHLC[], time: number): BarOHLC | null {
  if (!bars.length) return null;
  let best = bars[0];
  let bestD = Math.abs(bars[0].time - time);
  for (const b of bars) {
    const d = Math.abs(b.time - time);
    if (d < bestD) { best = b; bestD = d; }
  }
  return best;
}

// ═══════════════════════════════════════════════════════════════════════════
// HIT TESTING — in PIXELS, because that is what "click near it" means
// ═══════════════════════════════════════════════════════════════════════════
// Deliberately not in price space: a 6-unit tolerance is a rounding error on BTC and the
// entire chart on ETHBTC. The caller projects to pixels (the chart already owns that
// projection — see `ticketprimitive.ts` on why a second one drifts after a zoom) and this
// decides the geometry.

/** A drawing projected to screen space. `null` coords = off-scale, so unhittable. */
export type ProjectedDrawing = {
  id: string;
  kind: DrawingKind;
  points: ({ x: number; y: number } | null)[];
};

/** Distance from point P to the SEGMENT AB, in the same units as the inputs. */
export function distToSegment(
  px: number, py: number, ax: number, ay: number, bx: number, by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  // A degenerate segment is a point — the server refuses zero-extent drawings, but a
  // zoomed-out chart can still project two distinct bars onto the same pixel.
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  // Clamped projection: t in [0,1] keeps it a SEGMENT, so clicking far along the
  // extended line does not select it.
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Which drawing is under `(x, y)`, or null. Topmost-last wins on a tie.
 *
 * Per kind, and each is the geometry the user sees rather than a bounding box:
 *
 *  * `trendline` — the segment between the anchors;
 *  * `hline` — the horizontal ray from its anchor to the right edge (`paneW`), which is
 *    the shape it is DRAWN as. A full-pane test would select it from the left half of the
 *    chart where nothing is drawn;
 *  * `rect` — its EDGES, not its interior. Selecting by interior would swallow every
 *    click inside a large box, including on the candles the box exists to frame;
 *  * `fib` — any of its level lines, from the leg's left edge RIGHTWARDS to the pane
 *    edge, which is how they are drawn (a fib is forward-looking: bounding the levels at
 *    the leg would hide every one of them exactly where price is about to test it).
 *
 * A REAL DEFECT THIS CAUGHT: the fib branch first bounded x by `min..max` of the
 * projected points, and the primitive projected every level at the FIRST anchor's time —
 * so min == max and the whole fib was clickable only in a 6px column, while being drawn
 * across the pane. The shape you can click must be the shape you can see.
 */
export function hitTestDrawing(
  projected: readonly ProjectedDrawing[],
  x: number,
  y: number,
  paneW: number,
  tol: number = HIT_TOLERANCE_PX,
): string | null {
  let hit: string | null = null;
  for (const d of projected) {
    const pts = d.points.filter((p): p is { x: number; y: number } => p !== null);
    if (!pts.length) continue;
    let near = false;
    if (d.kind === 'trendline' && pts.length === 2) {
      near = distToSegment(x, y, pts[0].x, pts[0].y, pts[1].x, pts[1].y) <= tol;
    } else if (d.kind === 'hline') {
      near = Math.abs(y - pts[0].y) <= tol && x >= pts[0].x - tol && x <= paneW + tol;
    } else if (d.kind === 'rect' && pts.length === 2) {
      const [l, r] = [Math.min(pts[0].x, pts[1].x), Math.max(pts[0].x, pts[1].x)];
      const [t, b] = [Math.min(pts[0].y, pts[1].y), Math.max(pts[0].y, pts[1].y)];
      const onV = (Math.abs(x - l) <= tol || Math.abs(x - r) <= tol) && y >= t - tol && y <= b + tol;
      const onH = (Math.abs(y - t) <= tol || Math.abs(y - b) <= tol) && x >= l - tol && x <= r + tol;
      near = onV || onH;
    } else if (d.kind === 'fib') {
      // From the leg's left edge to the pane's right edge — the drawn shape.
      const l = Math.min(...pts.map((p) => p.x));
      near = x >= l - tol && x <= paneW + tol
        && pts.some((p) => Math.abs(y - p.y) <= tol);
    }
    if (near) hit = d.id;   // later drawings sit on top of earlier ones
  }
  return hit;
}

// ═══════════════════════════════════════════════════════════════════════════
// LABELS — what each drawing SAYS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A fib level's label: ratio AND price, per Andrei's reference (`0.618 (2,096.51)`).
 *
 * `fmt` is injected rather than chosen here so the price goes through the app's ONE
 * formatting rule (`overlays.priceText`), which derives its decimal count from the
 * value's own inputs. A local `toFixed(2)` printed `0.00 – 0.00` on 22.9% of OTE rows
 * before that rule existed, and every ratio pair in this app trades below 0.01.
 */
export function fibLabel(level: FibLevel, fmt: (v: number) => string): string {
  return `${level.ratio} (${fmt(level.price)})`;
}

/** A drawing's one-line description for the sidebar list. */
export function drawingLabel(d: Drawing, fmt: (v: number) => string): string {
  const magnet = d.magnet ? ' ⌁' : '';
  switch (d.kind) {
    case 'hline':
      return `hline ${fmt(d.anchors[0].price)}${magnet}`;
    case 'trendline':
      return `trend ${fmt(d.anchors[0].price)} → ${fmt(d.anchors[1].price)}${magnet}`;
    case 'rect': {
      const lo = Math.min(d.anchors[0].price, d.anchors[1].price);
      const hi = Math.max(d.anchors[0].price, d.anchors[1].price);
      return `rect ${fmt(lo)}–${fmt(hi)}${magnet}`;
    }
    case 'fib':
      // The DIRECTION is named, because it is the thing that inverts a fib and the
      // reason the ladder is computed server-side. `up` = the leg ran up (origin below
      // terminus), which puts the extensions above.
      return `fib ${d.anchors[0].price < d.anchors[1].price ? 'up' : 'down'} `
        + `${fmt(d.anchors[0].price)} → ${fmt(d.anchors[1].price)}${magnet}`;
  }
}

/** The disclosure row for the sidebar — silence about dropped drawings is the defect. */
export function drawingsSummary(p: DrawingsPayload | null): [string, string][] {
  if (!p) return [];
  const rows: [string, string][] = [['drawings', String(p.drawings.length)]];
  if (p.skipped === -1) {
    rows.push(['⚠ stored file', p.note ?? 'unusable — nothing loaded']);
  } else if (p.skipped > 0) {
    rows.push(['⚠ dropped', `${p.skipped} malformed — NOT drawn`]);
  }
  return rows;
}
